import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ComponentProps } from 'react';
import {
  Select,
  Box,
  MaterialSymbol,
  getMaskedNumber,
  withStyles,
  FieldData,
  formattedCurrency,
  Icon,
  AccountFundingOption,
  FundingSource,
  FundingSettlementTime,
  IsTodayDate,
  Skeleton,
} from 'vopay';
import { PaymentFieldDefault } from './types';
import styles from './TransactionCreationFields.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const classes = withStyles(styles);

type FundingOptionRow = {
  value: FundingSource;
  icon: Extract<ComponentProps<typeof MaterialSymbol>['icon'], 'payments' | 'account_balance' | 'credit_card'>;
  title: string;
  subtitle: string;
  settlementTime: string;
  fee: string;
  isFree: boolean;
};

const ICON_BY_SOURCE: Record<FundingSource, FundingOptionRow['icon']> = {
  balance: 'payments',
  bank: 'account_balance',
  credit: 'credit_card',
};

const ICON_FILL_BY_SOURCE: Record<FundingSource, number> = {
  balance: 0,
  bank: 1,
  credit: 0,
};

type FundingOptionFieldConfig = {
  fieldName: 'PayFrom' | 'Destination';
  translationPrefix: 'PayFrom' | 'Destination';
  labelDefaultValue: string;
  showFee: boolean;
  // PayFrom shows a right-aligned settlement-time badge per option; Destination's subtitle text
  // already states the settlement time, so the badge would just repeat it.
  showSettlementTime: boolean;
  excludeSources?: FundingSource[];
  getSubtitle: (option: AccountFundingOption) => string;
  // PayFrom echoes the live balance amount next to the selected value; Destination doesn't.
  showBalanceInlineSubtitle: boolean;
  // Extra visibility condition beyond the shared clientAccounts/request/scheduled gates below —
  // e.g. Destination also hides for Interac ('email'), which never settles at submit time.
  isAdditionallyVisible?: (values: Partial<PaymentFieldDefault>) => boolean;
};

export const useFundingOptionField = (
  fundingOptions: AccountFundingOption[],
  isFetchingFundingOptions: boolean,
  config: FundingOptionFieldConfig,
): FieldData<PaymentFieldDefault>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const {
    fieldName,
    translationPrefix,
    labelDefaultValue,
    showFee,
    showSettlementTime,
    excludeSources,
    getSubtitle,
    isAdditionallyVisible,
  } = config;

  const tr = (key: string, options: { defaultValue: string; count?: number }): string =>
    t(`Fields.${translationPrefix}.${key}`, options);

  const getSettlementTime = (settlementTime: FundingSettlementTime) => {
    if (settlementTime === 'immediately') return tr('instant', { defaultValue: 'Instant' });
    const days = Number(settlementTime.replace('t', ''));
    const key = days === 1 ? 'businessDaysOne' : 'businessDaysOther';
    return tr(key, { count: days, defaultValue: '{{count}} business day' });
  };

  const getFeeDisplay = (option: AccountFundingOption) => {
    const feePercent = Number(option.FeePercent || 0);
    const feePerTransaction = Number(option.FeePerTransaction || 0);
    if (feePercent > 0) return `${option.FeePercent}%`;
    if (feePerTransaction > 0) return `${option.Currency} ${formattedCurrency(option.FeePerTransaction || '0')}`;
    return tr('noFee', { defaultValue: 'No fee' });
  };

  const getTitle = (option: AccountFundingOption) => {
    if (option.Source === 'balance') return tr('vopayBalance', { defaultValue: 'VoPay Balance' });
    if (option.Source === 'bank') {
      return `${tr('bankAccount', { defaultValue: 'Bank Account' })} ${option.AccountNumber || ''}`;
    }
    return `${tr('creditCard', { defaultValue: 'Credit Card' })} ${getMaskedNumber(option.CreditCardNumber || '')}`;
  };

  const options: FundingOptionRow[] = fundingOptions
    .filter((option) => !excludeSources?.includes(option.Source))
    .map((option) => ({
      value: option.Source,
      icon: ICON_BY_SOURCE[option.Source],
      title: getTitle(option),
      subtitle: getSubtitle(option),
      settlementTime: getSettlementTime(option.SettlementTime),
      fee: getFeeDisplay(option),
      isFree: Number(option.FeePercent || 0) <= 0 && Number(option.FeePerTransaction || 0) <= 0,
    }));

  return [
    {
      label: () => tr('label', { defaultValue: labelDefaultValue }),
      fieldName,
      getInputState: () => 'required',
      showField: (values) => {
        const isScheduled =
          values.ScheduleType === 'recurring' ||
          (values.TakeFromAccountOn && !IsTodayDate(format(new Date(Number(values.TakeFromAccountOn)), 'yyyy-MM-dd')));
        return (
          !clientAccountsEnabled &&
          values.ProvideOrRequestPaymentInformation !== 'request' &&
          !isScheduled &&
          (isAdditionallyVisible?.(values) ?? true)
        );
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<FundingSource>({});
        if (isFetchingFundingOptions) {
          return <Skeleton height={32} />;
        }
        return (
          <Select
            version="2"
            sort={false}
            placeholder={tr('placeholder', { defaultValue: 'Please Select...' })}
            options={options}
            optionValue={(o) => o.value}
            {...fieldData}
            renderSelectedValue={(o, { Placeholder }) =>
              o ? (
                <Box ai="center">
                  <div className={classes('paymentName')}>{o.title} </div>
                  {config.showBalanceInlineSubtitle && o.value === 'balance' && (
                    <span style={{ paddingLeft: '5px' }}>· {o.subtitle}</span>
                  )}
                </Box>
              ) : (
                <Placeholder />
              )
            }
            renderOption={(o, { Option, selected }) => (
              <Option>
                <Box gapSize="12px" ai="center">
                  {selected ? (
                    <MaterialSymbol icon="check" size="20px" color="var(--vopayBlue)" />
                  ) : o.value === 'balance' ? (
                    <Icon width="18px" />
                  ) : (
                    <MaterialSymbol icon={o.icon} size="20px" color="#444" fillAmount={ICON_FILL_BY_SOURCE[o.value]} />
                  )}
                  <Box gapSize="2px" flx="col">
                    <div className={classes('paymentName', 'mediumWeight')}>{o.title}</div>
                    <div className={classes('paymentInfo')}>{o.subtitle}</div>
                  </Box>
                </Box>
                <Box flx="col" ai="fe" gapSize="4px">
                  {showSettlementTime && <div className={classes('smallFont')}>{o.settlementTime}</div>}
                  {showFee && <div className={classes(o.isFree && 'green', 'mediumWeight', 'smallFont')}>{o.fee}</div>}
                </Box>
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
};
